// src/services/sensorService.js
// Service module for Sensor related API calls - Added CRUD operations

// Import shared API helpers
import { authenticatedFetch } from './api';

import { getAuthToken } from '../utils/auth';

/**
 * Fetches all sensors from the backend.
 * @returns {Promise<Array>} A promise that resolves to an array of sensor objects.
 */
export const fetchSensors = async () => {
  const endpoint = '/sensors';
  try {
    const responseData = await authenticatedFetch(endpoint);
    // Standardize response handling: data is expected in a 'data' property or as a root array
    const sensors = Array.isArray(responseData?.data) ? responseData.data : 
                    Array.isArray(responseData) ? responseData : [];
    return sensors;
  } catch (error) {
    console.error("[SensorService] Error fetching sensors:", error);
    throw error;
  }
};

/**
 * Creates a new sensor record on the backend.
 * @param {object} sensorData - Data for the new sensor (e.g., { device_id, type, name, ... }).
 * @returns {Promise<object>} The newly created sensor object from the backend response.
 */
export const createSensor = async (sensorData) => {
  const endpoint = '/sensors';
  console.log("[SensorService] Creating sensor");
  const options = {
    method: 'POST',
    body: JSON.stringify(sensorData),
  };
  const result = await authenticatedFetch(endpoint, options);
  return result?.sensor || result;
};

/**
 * Fetches a single sensor by its device ID.
 * @param {string} deviceId - The unique device ID of the sensor.
 * @returns {Promise<object|null>} The sensor object or null if not found.
 */
export const fetchSensorById = async (deviceId) => {
    // 1. Parameter Validation
    if (!deviceId) {
        console.warn("[SensorService] fetchSensorById called without deviceId.");
        return null;
    }

    // 2. 🚀 ENTERPRISE UPDATE: Pre-flight Authentication Check
    // Prevents making an unauthenticated network request that will inevitably fail.
    const token = typeof getAuthToken === 'function' ? getAuthToken() : localStorage.getItem('token');
    if (!token) {
        const authError = new Error("Authentication token missing.");
        console.error(`[SensorService] Aborting fetch for sensor ${deviceId}:`, authError.message);
        throw authError; // Allow the UI to catch this and redirect to login
    }

    const endpoint = `/sensors/${deviceId}`;
    console.log(`[SensorService] Fetching sensor by ID: ${deviceId} from: ${endpoint}`);

    try {
        // 3. Authenticated Request
        const result = await authenticatedFetch(endpoint);
        
        // 4. Normalized Data Mapping
        // Returns the nested sensor object if present, otherwise returns the direct result.
        return result?.sensor || result?.data || result;

    } catch (error) {
        // 5. Specialized Error Handling
        if (error.message && (error.message.includes('404') || error.message.includes('not found'))) {
            console.log(`[SensorService] Sensor with ID ${deviceId} not found in database.`);
            return null;
        }
        
        if (error.message && error.message.includes('Authentication')) {
            console.error("[SensorService] Session expired or invalid token during sensor fetch.");
        } else {
            console.error(`[SensorService] Error fetching sensor by ID ${deviceId}:`, error);
        }

        throw error;
    }
};


/**
 * Updates the details of a specific sensor.
 * @param {string} deviceId - The ID of the sensor to update.
 * @param {object} updateData - An object containing the fields to update (e.g., { latitude, longitude }).
 * @returns {Promise<object>} The updated sensor object.
 */
export const updateSensorDetails = async (deviceId, updateData) => {
  const endpoint = `/sensors/${deviceId}`;
  console.log(`[SensorService] Updating sensor ${deviceId} at: ${endpoint}`);
  const options = {
    method: 'PATCH', 
    body: JSON.stringify(updateData),
  };
  try {
    const result = await authenticatedFetch(endpoint, options);
    return result?.sensor || result;
  } catch (error) {
    console.error(`[SensorService] Error updating sensor ${deviceId}:`, error);
    throw error;
  }
};

/**
 * Deletes a sensor by its device ID.
 * @param {string} deviceId - The unique device ID of the sensor to delete.
 * @returns {Promise<void>} A promise that resolves on successful deletion.
 */
export const deleteSensor = async (deviceId) => {
  if (!deviceId) {
    console.warn("[SensorService] deleteSensor called without deviceId.");
    throw new Error("Sensor Device ID is required for deletion.");
  }
  const endpoint = `/sensors/${deviceId}`;
  console.log(`[SensorService] Deleting sensor with ID: ${deviceId}`);
  const options = { method: 'DELETE' };
  
  await authenticatedFetch(endpoint, options);
  
  console.log(`[SensorService] Sensor with ID ${deviceId} deleted successfully.`);
};

/**
 * Fetches historical PSI readings for all sensors at a given timestamp.
 * @param {Date|string} timestamp - A JS Date object or ISO string representing the desired time.
 * @returns {Promise<object>} A mapping of sensor IDs to PSI values.
 */
export const fetchHistoricalReadings = async (timestamp) => {
  const isoTime = typeof timestamp === 'string' ? timestamp : new Date(timestamp).toISOString();
  const endpoint = `/sensors/historical?timestamp=${encodeURIComponent(isoTime)}`;
  console.log(`[SensorService] Fetching historical readings for: ${isoTime}`);

  try {
    const result = await authenticatedFetch(endpoint);
    if (!result || typeof result !== 'object') {
      throw new Error("Invalid historical readings format");
    }
    return result;
  } catch (error) {
    console.error("[SensorService] Error fetching historical readings:", error);
    throw error;
  }
};

/**
 * Assigns a specific sensor to an asset.
 * @param {string} deviceId The sensor's device ID.
 * @param {string} assetId The asset's ID.
 */
export const assignSensorToAsset = async (deviceId, assetId) => {
  try {
    const response = await authenticatedFetch(`/sensors/${deviceId}/assign`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assetId }),
    });
    return response;
  } catch (error) {
    console.error('Error assigning sensor:', error);
    throw error;
  }
};

/**
 * Fetches only sensors that have not yet been placed on the map.
 * @returns {Promise<Array>} A promise that resolves to an array of unplaced sensor objects.
 */
export const fetchUnplacedSensors = async () => {
  const endpoint = '/sensors?unplaced=true';
  console.log(`[SensorService] Fetching unplaced sensors from: ${endpoint}`);
  try {
    const responseData = await authenticatedFetch(endpoint);
    // Handle backend response where data might be nested or root
    const sensors = Array.isArray(responseData?.data) ? responseData.data : 
                    Array.isArray(responseData) ? responseData : [];
    console.log(`[SensorService] Fetched ${sensors.length} unplaced sensors.`);
    return sensors;
  } catch (error) {
    console.error("[SensorService] Error fetching unplaced sensors:", error);
    throw error;
  }
};

// --- NEWLY ADDED FUNCTIONS ---

/**
 * Place a sensor at specific coordinates (helper for updateSensorDetails)
 * @param {string} sensorId - The sensor ID
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<Object>} Updated sensor object
 */
export async function placeSensor(sensorId, latitude, longitude) {
  // Validation
  if (latitude === undefined || longitude === undefined) {
    throw new Error('Latitude and longitude are required');
  }
  if (latitude < -90 || latitude > 90) {
    throw new Error('Latitude must be between -90 and 90');
  }
  if (longitude < -180 || longitude > 180) {
    throw new Error('Longitude must be between -180 and 180');
  }

  return updateSensorDetails(sensorId, {
    latitude,
    longitude,
    placed_at: new Date().toISOString(),
  });
}

/**
 * Link a sensor to a pipeline
 * @param {string} sensorId - The sensor ID
 * @param {string} pipelineId - The pipeline ID
 * @returns {Promise<Object>} Link response
 */
export const linkSensorToPipeline = async (sensorId, pipelineId) => {
  console.log(`[SensorService] Linking sensor ${sensorId} to pipeline ${pipelineId}`);
  const endpoint = `/sensors/${sensorId}/link-pipeline`;
  const options = {
    method: 'POST',
    body: JSON.stringify({ pipeline_id: pipelineId }),
  };
  try {
    return await authenticatedFetch(endpoint, options);
  } catch (error) {
    console.error(`[SensorService] Error linking sensor ${sensorId}:`, error);
    throw error;
  }
};

/**
 * Unlink a sensor from its pipeline
 * @param {string} sensorId - The sensor ID
 * @returns {Promise<Object>} Unlink response
 */
export const unlinkSensorFromPipeline = async (sensorId) => {
  console.log(`[SensorService] Unlinking sensor ${sensorId}`);
  const endpoint = `/sensors/${sensorId}/unlink-pipeline`;
  const options = { method: 'DELETE' };
  try {
    return await authenticatedFetch(endpoint, options); // Assumes 204/OK
  } catch (error) {
    console.error(`[SensorService] Error unlinking sensor ${sensorId}:`, error);
    throw error;
  }
};

/**
 * Fetch sensor-pipeline links
 * @returns {Promise<Object>} Object mapping sensor IDs to pipeline IDs
 */
export const fetchSensorPipelineLinks = async () => {
  console.log(`[SensorService] Fetching sensor-pipeline links`);
  const endpoint = '/sensors/pipeline-links';
  try {
    const data = await authenticatedFetch(endpoint);
    return data.links || data || {};
  } catch (error) {
    if (error.message && error.message.includes('HTTP error 404')) {
      console.warn('[SensorService] Sensor-pipeline links endpoint not found (404), returning empty object.');
      return {};
    }
    console.error("[SensorService] Error fetching sensor-pipeline links:", error);
    throw error;
  }
};

/**
 * Fetch sensors for a specific pipeline
 * @param {string} pipelineId - The pipeline ID
 * @returns {Promise<Array>} Array of sensor objects
 */
export const fetchSensorsByPipeline = async (pipelineId) => {
  console.log(`[SensorService] Fetching sensors for pipeline ${pipelineId}`);
  const endpoint = `/pipelines/${pipelineId}/sensors`;
  try {
    const data = await authenticatedFetch(endpoint);
    return data.sensors || data || [];
  } catch (error) {
    console.error(`[SensorService] Error fetching sensors for pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Fetch sensor reading history
 * @param {string} sensorId - The sensor ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of reading objects
 */
export const fetchSensorHistory = async (sensorId, options = {}) => {
  console.log(`[SensorService] Fetching history for sensor ${sensorId}`);
  const params = new URLSearchParams();
  if (options.startDate) params.append('start', options.startDate.toISOString());
  if (options.endDate) params.append('end', options.endDate.toISOString());
  if (options.limit) params.append('limit', options.limit);
  
  const endpoint = `/sensors/${sensorId}/history?${params.toString()}`;
  try {
    const data = await authenticatedFetch(endpoint);
    // --- UPDATE: the API answers { success, data: [...] } — returning the wrapper object broke charts
    return Array.isArray(data) ? data : (data?.data || data?.readings || []);
  } catch (error) {
    console.error(`[SensorService] Error fetching history for sensor ${sensorId}:`, error);
    throw error;
  }
};

// --- UPDATE (sensors v2) ------------------------------------------------------
/**
 * Fetches sensors without the AI predictions (faster — readings and online state only).
 * @returns {Promise<Array>}
 */
export const fetchSensorsLite = async () => {
  const responseData = await authenticatedFetch('/sensors?ai=0');
  return Array.isArray(responseData?.data) ? responseData.data : Array.isArray(responseData) ? responseData : [];
};

/**
 * Everything about one sensor: linked asset, last 48 readings, open work orders, active alerts, AI.
 * @param {string} deviceId
 * @returns {Promise<{sensor, asset, readings, open_work_orders, active_alerts}>}
 */
export const fetchSensorContext = async (deviceId) => {
  if (!deviceId) return null;
  const res = await authenticatedFetch(`/sensors/${encodeURIComponent(deviceId)}/context`);
  return res?.data || res;
};

/**
 * Links a sensor to an asset (pump, well, tank, …) — the link asset health and work orders use.
 * @param {string} deviceId
 * @param {string} assetId
 * @param {string} [entityType]
 */
export const linkSensorToAsset = async (deviceId, assetId, entityType) => {
  const res = await authenticatedFetch(`/sensors/${encodeURIComponent(deviceId)}/assign`, {
    method: 'PATCH',
    body: JSON.stringify({ assetId, ...(entityType ? { entityType } : {}) }),
  });
  return res?.data || res;
};
// --- END UPDATE ---------------------------------------------------------------

/**
 * Batch update multiple sensors
 * @param {Array<Object>} updates - Array of update objects
 * @returns {Promise<Array>} Array of updated sensors
 */
export const batchUpdateSensors = async (updates) => {
  console.log(`[SensorService] Batch updating ${updates.length} sensors`);
  const endpoint = '/sensors/batch-update';
  const options = {
    method: 'POST',
    body: JSON.stringify({ updates }),
  };
  try {
    const data = await authenticatedFetch(endpoint, options);
    return data.sensors || data || [];
  } catch (error) {
    console.error(`[SensorService] Error batch updating sensors:`, error);
    throw error;
  }
};